import { useState, type ReactNode } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Save } from "lucide-react";

import {
  OpsWorkspaceAddressUpdateSchema,
  type OpsCustomerSummary,
  type OpsWorkspaceAddressUpdate,
} from "../../shared/operations-schemas";
import { Button } from "../components/ui/button";
import { Field, FieldDescription, FieldError, FieldLabel } from "../components/ui/field";
import { Input } from "../components/ui/input";
import { SheetBody, SheetFooter, SheetForm } from "../components/ui/sheet";
import { updateCustomerWorkspaceAddress } from "./api";

function workspaceSuffix(customer: OpsCustomerSummary): string {
  const hostname = new URL(customer.workspaceUrl).hostname;
  const suffix = hostname.slice(customer.subdomain.length);
  return suffix.startsWith(".") || suffix.startsWith("-") ? suffix : ".das.dev";
}

function useAddressState(customer: OpsCustomerSummary) {
  const queryClient = useQueryClient();
  const [subdomain, setSubdomain] = useState(customer.subdomain);
  const [validationError, setValidationError] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: (input: OpsWorkspaceAddressUpdate) =>
      updateCustomerWorkspaceAddress(customer.slug, input),
    onSuccess: async (response) => {
      setSubdomain(response.customer.subdomain);
      queryClient.setQueryData(["ops", "customers", "detail", customer.slug], response);
      await queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === "ops" &&
          query.queryKey[1] === "customers" &&
          typeof query.queryKey[2] === "object",
      });
    },
  });
  return { subdomain, setSubdomain, validationError, setValidationError, mutation };
}

/** The workspace address form as a side sheet's body and footer (ADR-0087). */
export function CustomerWorkspaceAddress({
  customer,
  back,
}: {
  customer: OpsCustomerSummary;
  /** A Back action for the footer's start slot when the sheet had other content first. */
  back?: ReactNode;
}) {
  const { subdomain, setSubdomain, validationError, setValidationError, mutation } =
    useAddressState(customer);
  const suffix = workspaceSuffix(customer);

  return (
    <SheetForm
      aria-label="Workspace address"
      onSubmit={(event) => {
        event.preventDefault();
        mutation.reset();
        const parsed = OpsWorkspaceAddressUpdateSchema.safeParse({ subdomain });
        if (!parsed.success) {
          setValidationError(parsed.error.issues[0]?.message ?? "Enter a valid address.");
          return;
        }
        setValidationError(null);
        mutation.mutate(parsed.data);
      }}
    >
      <SheetBody>
        <Field data-invalid={validationError ? true : undefined}>
          <FieldLabel htmlFor="customer-workspace-subdomain">Workspace subdomain</FieldLabel>
          <div className="flex items-center gap-2">
            <Input
              aria-invalid={validationError ? true : undefined}
              autoCapitalize="none"
              autoComplete="off"
              id="customer-workspace-subdomain"
              onChange={(event) => {
                setSubdomain(event.target.value);
                setValidationError(null);
                mutation.reset();
              }}
              spellCheck={false}
              value={subdomain}
            />
            <span className="shrink-0 font-mono text-sm text-muted-foreground">{suffix}</span>
          </div>
          <FieldDescription>
            Previous addresses remain aliases, so existing bookmarks continue to work.
          </FieldDescription>
          <FieldError>{validationError ?? mutation.error?.message}</FieldError>
        </Field>
      </SheetBody>
      <SheetFooter
        start={
          <>
            {back}
            {mutation.isSuccess ? (
              <p aria-live="polite" className="m-0 text-sm font-semibold text-success">
                Workspace address saved.
              </p>
            ) : null}
          </>
        }
      >
        <Button disabled={mutation.isPending || subdomain === customer.subdomain} type="submit">
          <Save /> {mutation.isPending ? "Saving…" : "Save address"}
        </Button>
      </SheetFooter>
    </SheetForm>
  );
}
